// FACTURACION-ASEGURADORAS-DETALLE-08: modal "Ver detalle" de una factura.
// Para FAC de paciente/cliente muestra cabecera + líneas fiscales.
// Para FAC de aseguradora (tipo_pagador SEGURO / histórica) añade la
// aseguradora, los pacientes incluidos y la tabla de prestaciones (PCP)
// reconstruida server-side desde facturas_detalle_procedimientos -- funciona
// igual en modo DETALLADA y RESUMIDA.
import { useCallback, useEffect, useState } from "react";
import { Col, Modal, Row, Badge, Spinner, Table } from "react-bootstrap";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { GetInvoiceDetalle } from "../../../Service/Service.billing";

const money = (n) => Number(n ?? 0).toFixed(2);

function DetalleFactura({ data }) {
  const { show, handleClose, invoice } = data;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);

  const fetchDetalle = useCallback(async (idfactura) => {
    setLoading(true);
    setError(null);
    setDetalle(null);
    const res = await GetInvoiceDetalle(idfactura);
    if (res && res.success) {
      setDetalle(res.data);
    } else {
      setError(res?.message || "No se pudo cargar el detalle de la factura.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (show && invoice?.idfactura) {
      fetchDetalle(invoice.idfactura);
    }
  }, [show, invoice, fetchDetalle]);

  const cab = detalle?.cabecera;
  const esAseguradora = !!cab?.es_factura_aseguradora;

  return (
    <Modal show={show} backdrop="static" onHide={handleClose} keyboard={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Detalle factura {invoice?.num_documento || ""}{" "}
          <small className="text-muted">id: {invoice?.idfactura}</small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center p-4">
            <Spinner animation="border" role="status" size="sm" /> Cargando detalle...
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger mb-0">{error}</div>
        )}

        {!loading && !error && cab && (
          <>
            <Row className="mb-2">
              <Col sm={6}><b>Documento:</b> {cab.documento} {cab.num_documento}</Col>
              <Col sm={6}>
                <b>Fecha:</b>{" "}
                {cab.fecha_emision
                  ? format(new Date(cab.fecha_emision), "dd/MM/yyyy", { locale: es })
                  : "—"}
              </Col>
              <Col sm={6}><b>Receptor:</b> {cab.receptor?.nombre || "—"}</Col>
              <Col sm={6}><b>Tipo pagador:</b> {cab.tipo_pagador || "—"}</Col>
              <Col sm={6}>
                <b>Estado:</b>{" "}
                {cab.estado === "A"
                  ? <Badge bg="success">Activo</Badge>
                  : <Badge bg="danger">Anulada</Badge>}
              </Col>
              <Col sm={6}>
                <b>Estado Cont.:</b>{" "}
                {cab.id_contifico
                  ? <Badge bg="primary">En contífico</Badge>
                  : <Badge bg="warning">No registrado</Badge>}
              </Col>
              {esAseguradora && (
                <>
                  <Col sm={6}><b>Aseguradora:</b> {cab.receptor?.nombre || "—"}</Col>
                  <Col sm={6}>
                    <b>Modo:</b> {cab.modo_almacenamiento || "—"}
                    {cab.es_multipaciente && (
                      <Badge bg="info" className="ms-2">Multipaciente</Badge>
                    )}
                  </Col>
                </>
              )}
              {cab.info_adicional && (
                <Col sm={12}><b>Info adicional:</b> {cab.info_adicional}</Col>
              )}
            </Row>

            <Row className="mb-3">
              <Col sm={4}><b>Subtotal:</b> {money(cab.subtotal)}</Col>
              <Col sm={4}><b>IVA:</b> {money(cab.iva)}</Col>
              <Col sm={4}><b>Total:</b> {money(cab.total)}</Col>
            </Row>

            {esAseguradora && detalle.pacientes_incluidos?.length > 0 && (
              <div className="mb-3">
                <b>Pacientes incluidos ({detalle.pacientes_incluidos.length}):</b>{" "}
                {detalle.pacientes_incluidos.map((p) => (
                  <Badge bg="secondary" className="me-1" key={p.idpaciente}>{p.nombre}</Badge>
                ))}
              </div>
            )}

            <h6 className="mb-2">Líneas de la factura</h6>
            <Table size="sm" bordered responsive>
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th className="text-end">Cantidad</th>
                  <th className="text-end">Precio unitario</th>
                  <th className="text-end">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {detalle.lineas?.length ? detalle.lineas.map((l) => (
                  <tr key={l.iddfactura}>
                    <td>{l.codigo ? `${l.codigo} · ` : ""}{l.descripcion}</td>
                    <td className="text-end">{Number(l.cantidad).toFixed(0)}</td>
                    <td className="text-end">{money(l.precio_unitario)}</td>
                    <td className="text-end">{money(l.subtotal)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={4} className="text-center text-muted">Sin líneas</td></tr>
                )}
              </tbody>
            </Table>

            {esAseguradora && (
              <>
                <h6 className="mb-2 mt-3">
                  Prestaciones incluidas ({detalle.prestaciones?.length || 0})
                </h6>
                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Paciente</th>
                      <th>Prestación</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Valor</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.prestaciones?.length ? detalle.prestaciones.map((p) => (
                      <tr key={p.idprocedimiento_cita_paciente}>
                        <td>{p.paciente}</td>
                        <td>{p.codigo ? `${p.codigo} · ` : ""}{p.prestacion}</td>
                        <td className="text-end">{Number(p.cantidad).toFixed(0)}</td>
                        <td className="text-end">{money(p.valor)}</td>
                        <td>{p.estado || "—"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={5} className="text-center text-muted">
                        Sin prestaciones vinculadas (factura histórica sin trazabilidad forward)
                      </td></tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-secondary btn-sm" onClick={handleClose}>Cerrar</button>
      </Modal.Footer>
    </Modal>
  );
}

export default DetalleFactura;
